import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Home, Layers, Package, ShoppingCart, Ticket, User } from "lucide-react";
import { isMarketplaceLive } from "../../lib/marketplace";
import { useAuth } from "../../stores/auth";
import { useMobileChrome } from "../../stores/mobileChrome";
import { useBodyLock } from "../../hooks/useBodyLock";
import { sfx } from "../../lib/sfx";
import { cartBadgeCount, cartNavAriaLabel, cartNavPath, formatCartBadge } from "../../lib/cart-badge";

interface MobileBottomNavProps {
  cartCount: number;
  pendingPaymentCount: number;
}

function quickActions(moduleEnabled: (k: string) => boolean, marketplaceLive: boolean) {
  const items = [
    { to: "/raffles", label: "Raffles", image: "/raffle.png", module: "raffles" as const },
    { to: "/packs", label: "Packs", image: "/pack.png", module: "packs" as const },
    { to: "/shop", label: "Shop", image: "/shop.png", module: "ecommerce" as const },
    ...(marketplaceLive
      ? [{ to: "/market", label: "Market", image: "/shop.png", module: "marketplace" as const }]
      : []),
    { to: "/auctions", label: "Auctions", image: "/auction.png", module: "auctions" as const },
  ];
  return items.filter((a) => moduleEnabled(a.module)).slice(0, 4);
}

/**
 * Fixed five-slot mobile tab bar: Home, Inventory, a raised gacha-capsule
 * launcher, Cart, Profile. Tapping the capsule pops open a picker for the
 * four main destinations.
 */
export function MobileBottomNav({ cartCount, pendingPaymentCount }: MobileBottomNavProps) {
  const { user, moduleEnabled } = useAuth();
  const [quickOpen, setQuickOpen] = useState(false);
  const bottomNavHidden = useMobileChrome((s) => s.bottomNavSuppressed > 0);

  let fourthSlot: { to: string; label: string; icon: typeof ShoppingCart } = {
    to: "/account",
    label: "Profile",
    icon: User,
  };
  if (moduleEnabled("ecommerce")) {
    fourthSlot = { to: "/cart", label: "Cart", icon: ShoppingCart };
  } else if (moduleEnabled("raffles")) {
    fourthSlot = { to: "/raffles", label: "Raffles", icon: Ticket };
  }

  const collectionOn = moduleEnabled("collection");
  let inventorySlot: { to: string; label: string; icon: typeof Layers } = {
    to: user ? "/orders" : "/login",
    label: "Orders",
    icon: Package,
  };
  if (collectionOn) {
    inventorySlot = {
      to: user ? "/inventory" : "/login",
      label: "Inventory",
      icon: Layers,
    };
  }

  return (
    <>
      <QuickActionSheet open={quickOpen} onClose={() => setQuickOpen(false)} />

      <motion.nav
        aria-hidden={bottomNavHidden}
        animate={{ y: bottomNavHidden ? "100%" : 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 32 }}
        className="fixed inset-x-0 bottom-0 z-[46] lg:hidden"
      >
        <div className="mobile-nav-shell">
          <div className="mobile-nav-seam" aria-hidden />
          <div
            className="relative z-[1] mx-auto flex max-w-lg items-stretch pb-[env(safe-area-inset-bottom)]"
            style={{ paddingTop: "var(--mobile-nav-capsule-pt)" }}
          >
            <Tab to="/" label="Home" icon={Home} end />

            <Tab
              to={inventorySlot.to}
              label={inventorySlot.label}
              icon={inventorySlot.icon}
            />

            <div className="relative flex w-20 shrink-0 justify-center">
              <CapsuleButton open={quickOpen} onToggle={() => setQuickOpen((v) => !v)} />
            </div>

            {moduleEnabled("ecommerce") ? (
              <CartTab cartCount={cartCount} pendingPaymentCount={pendingPaymentCount} />
            ) : (
              <Tab to={fourthSlot.to} label={fourthSlot.label} icon={fourthSlot.icon} />
            )}
            <Tab to={user ? "/account" : "/login"} label="Profile" icon={User} />
          </div>
        </div>
      </motion.nav>
    </>
  );
}

/** Raised centre button — a two-tone gacha capsule, like the machine's own release button. */
function CapsuleButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-label="Quick actions"
      aria-expanded={open}
      onClick={onToggle}
      className="absolute z-[2] flex items-center justify-center rounded-full shadow-xl shadow-brand-pink/40 transition-transform active:scale-90"
      style={{
        top: "calc(-1 * var(--mobile-nav-capsule-lift))",
        width: "var(--mobile-nav-capsule-size)",
        height: "var(--mobile-nav-capsule-size)",
      }}
    >
      {!open && <span className="absolute inset-0 rounded-full pulse-ring" aria-hidden />}
      <motion.div animate={{ rotate: open ? 45 : 0 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}>
        <GachaCapsuleIcon size={52} />
      </motion.div>
    </button>
  );
}

/** The capsule's pop-up: pick a destination instead of jumping straight to one. */
function QuickActionSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { moduleEnabled, config } = useAuth();
  useBodyLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const options = quickActions(moduleEnabled, isMarketplaceLive(config, moduleEnabled));
  if (!options.length) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[45] bg-black/65 backdrop-blur-sm lg:hidden"
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Quick actions"
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 340, damping: 28 }}
            className="fixed inset-x-4 bottom-[calc(var(--mobile-nav-height)+env(safe-area-inset-bottom))] z-[45] rounded-3xl border border-white/10 bg-brand-dark/95 p-4 shadow-2xl backdrop-blur-xl lg:hidden"
          >
            <p className="mb-3 text-center font-display text-[10px] font-bold uppercase tracking-[0.3em] text-brand-pink">
              Where to?
            </p>
            <div className="grid grid-cols-4 gap-1.5">
              {options.map((option) => (
                <button
                  key={option.to}
                  type="button"
                  onClick={() => {
                    sfx.tap();
                    navigate(option.to);
                    onClose();
                  }}
                  className="flex flex-col items-center gap-1.5 rounded-2xl p-2 transition-colors active:scale-95 active:bg-white/5"
                >
                  <img src={option.image} alt="" draggable={false} className="h-12 w-12 object-contain drop-shadow-[0_4px_14px_rgba(221,84,235,0.4)]" />
                  <span className="text-center text-[10px] font-semibold text-white/85">{option.label}</span>
                </button>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function GachaCapsuleIcon({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden>
      <defs>
        <linearGradient id="capsuleTop" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffb0f2" />
          <stop offset="100%" stopColor="#dd54eb" />
        </linearGradient>
        <linearGradient id="capsuleBottom" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#e7def4" />
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="46" fill="url(#capsuleBottom)" />
      <path d="M4 50a46 46 0 0 1 92 0Z" fill="url(#capsuleTop)" />
      <rect x="4" y="47.5" width="92" height="5" fill="#2a0f42" />
      <circle cx="50" cy="50" r="46" fill="none" stroke="#2a0f42" strokeWidth="3" />
      <circle cx="33" cy="29" r="7" fill="#ffffff" fillOpacity="0.55" />
    </svg>
  );
}

function CartTab({ cartCount, pendingPaymentCount }: { cartCount: number; pendingPaymentCount: number }) {
  const cartBadge = cartBadgeCount(cartCount, pendingPaymentCount);

  return (
    <NavLink
      to={cartNavPath(cartCount, pendingPaymentCount)}
      aria-label={cartNavAriaLabel(cartCount, pendingPaymentCount)}
      className={({ isActive }) =>
        `relative flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${
          isActive ? "text-brand-pink" : "text-white/45"
        }`
      }
    >
      <span className="relative p-1">
        <ShoppingCart size={22} strokeWidth={2} className="opacity-90" />
        {cartBadge > 0 && (
          <span
            className={`absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold ${
              pendingPaymentCount > 0 ? "bg-amber-400 text-brand-dark" : "bg-brand-blue text-white"
            }`}
          >
            {formatCartBadge(cartBadge)}
          </span>
        )}
      </span>
      Cart
    </NavLink>
  );
}

function Tab({
  to,
  label,
  icon: Icon,
  end = false,
}: {
  to: string;
  label: string;
  icon: typeof Home;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `relative flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${
          isActive ? "text-brand-pink" : "text-white/45"
        }`
      }
    >
      <span className="p-1">
        <Icon size={22} strokeWidth={2} className="opacity-90" />
      </span>
      {label}
    </NavLink>
  );
}
